import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import PageLayout from "../../components/PageLayout";
import Card from "../../components/Card";
import AnonymousRequestIntro from "./AnonymousRequestIntro";
import AnonymousRequestForm from "./AnonymousRequestForm";
import AnonymousDocumentUploadFlow from "./AnonymousDocumentUploadFlow";
import { normaliseInformationRequestConfiguration } from "../../shared/informationRequest/informationRequestConfiguration";
import { reportDoableLoadFailure } from "../../utils/sentryUtils";

/**
 * Generic flow for anonymous doable requests.
 * Shows an intro screen first, then the appropriate form.
 */
const AnonymousRequestFlow = () => {
  const { doableId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [doable, setDoable] = useState(null);
  const [hideIntro, setHideIntro] = useState(false);
  const hasIntroScreen = doable?.configuration?.intro_screen;
  const shouldShowIntro = hasIntroScreen && !hideIntro;

  useEffect(() => {
    const controller = new AbortController();
    const fetchDoable = async () => {
      try {
        const resp = await fetch(`/api/anonymous_doable/${doableId}`, { signal: controller.signal });

        if (!resp.ok) {
          reportDoableLoadFailure(resp, { action: "LoadAnonymousDoable", doableId });
          if (resp.status === 404) {
            setError("not found");
          } else {
            setError("Failed to load request details");
          }
          setLoading(false);
          return;
        }

        const data = await resp.json();

        if (data.status === "expired") {
          setError("This request has expired");
        } else if (data.status === "complete") {
          setError("You’ve already submitted this form");
        } else if (!data.status || !data.configuration) {
          setError("Failed to load request details");
        } else if (data.doable_type !== "information_request" && data.doable_type !== "document_request") {
          setError(`Request type not supported: ${data.doable_type}`);
        } else {
          // Normalise information requests at this boundary. Document requests keep
          // their own shape; adapter errors follow the fetch-failure path.
          setDoable(
            data.doable_type === "information_request"
              ? { ...data, configuration: normaliseInformationRequestConfiguration(data.configuration) }
              : data,
          );
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Failed to load request details", err);
        reportDoableLoadFailure(err, { action: "LoadAnonymousDoable", doableId });
        setError("Failed to load request details");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchDoable();
    return () => controller.abort();
  }, [doableId]);

  const handleContinueFromIntro = () => {
    setHideIntro(true);
  };

  if (loading) {
    return (
      <PageLayout pageWidth="wide">
        <Card title="Loading..." removeTopPadding>
          <p>Please wait while we load your form...</p>
        </Card>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout pageWidth="wide">
        <Card title="Sorry, that didn’t work" removeTopPadding>
          {error === "not found" ? (
            <p>We couldn’t find that request. Please check the link and try again.</p>
          ) : (
            <p>{error}</p>
          )}
        </Card>
      </PageLayout>
    );
  }

  if (shouldShowIntro) {
    const introScreen = doable.configuration.intro_screen;
    return (
      <PageLayout pageWidth="wide">
        <AnonymousRequestIntro
          title={introScreen.title}
          description={introScreen.description}
          linkText={introScreen?.link_text}
          onContinue={handleContinueFromIntro}
        />
      </PageLayout>
    );
  }

  if (doable.doable_type === "document_request") {
    return (
      <PageLayout pageWidth="wide">
        <AnonymousDocumentUploadFlow doable={doable} pageWidth="wide" />
      </PageLayout>
    );
  }

  return (
    <PageLayout pageWidth="wide">
      <AnonymousRequestForm doable={doable} pageWidth="wide" />
    </PageLayout>
  );
};

export default AnonymousRequestFlow;
